/**
 * NE4 (.334): Gebiete und Teamtage, Mailtexte, Läufe; die GBGS-Handliste.
 *
 * ORIGINALTEXT MARVIN (08.10.2026)
 *   „Die Gebietsliste, Postleitzahlen gehören zusammen, das musst du selber
 *    filtern. Du siehst ja selber, welche Kunden dieselbe Postleitzahl haben."
 *   „GBGS, das ist eine ganz normale Website. Mit Login, Benutzername bzw.
 *    E-Mail und Passwort, keine Zwei-Faktor-Authentifizierung."
 */
import { useEffect, useMemo, useState } from 'react';
import { api, darf, datum, zeitpunkt, type Anmelder } from '../api.ts';
import { Suchfeld } from '../teile/Schluesselsuche.tsx';
import { connectAbgleich } from '../teile/ne4abgleich.ts';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, Haken, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { tagLang, uhr } from './Ne4Arbeit.tsx';
import { eingeben, fragen } from '../teile/rueckfrage.tsx';
import { SchlangeTabelle, WartegrundFenster } from './Gbgs.tsx';
import { Dateifenster } from '../teile/Dateifenster.tsx';

type Reiter = 'gebiete' | 'teamtage' | 'mails' | 'laeufe';

export function Ne4Planung({ ich }: { ich: Anmelder }) {
  const [reiter, setReiter] = useState<Reiter>('teamtage');
  return (
    <>
      <Kopf titel="NE4, Gebiete und Teamtage" weg="Wo die Teams hinfahren, und wann" />
      <div className="reiter" style={{ marginBottom: 12 }}>
        {([['teamtage', 'Teamtage'], ['gebiete', 'Gebiete'], ['mails', 'Mails an Kunden'], ['laeufe', 'Läufe']] as [Reiter, string][]).map(([k, t]) => (
          <button key={k} type="button" aria-current={reiter === k} onClick={() => setReiter(k)}>{t}</button>
        ))}
      </div>
      <div className="inhalt">
      {reiter === 'teamtage' && <Teamtage />}
      {reiter === 'gebiete' && <Gebiete />}
      {reiter === 'mails' && <Mailtexte ich={ich} />}
      {reiter === 'laeufe' && <Laeufe />}
      </div>
    </>
  );
}

/* ================================================================== Teamtage */

function Teamtage() {
  const heute = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const [von, setVon] = useState(heute);
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [zelle, setZelle] = useState<{ team: any; tag: string; alt: any | null } | null>(null);
  const laden = () => api.ne4Teamtage(von).then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [von]);
  useFrisch(laden);
  const tage = useMemo(() => {
    const raus: string[] = []; const s = new Date(von + 'T12:00:00');
    for (let i = 0; i < 21; i++) { const x = new Date(s); x.setDate(s.getDate() + i); if (x.getDay() !== 0 && x.getDay() !== 6) raus.push(x.toLocaleDateString('sv-SE')); }
    return raus;
  }, [von]);
  const schieben = (n: number) => { const x = new Date(von + 'T12:00:00'); x.setDate(x.getDate() + n); setVon(x.toLocaleDateString('sv-SE')); };
  const eintrag = (teamId: string, tag: string) => (d?.tage ?? []).find((t: any) => t.team_id === teamId && t.tag === tag);
  async function neuPlanen() {
    setFehler(null); setMeldung(null);
    try { const r = await api.ne4GebieteNeu(); setMeldung(`Gebiete: ${r.neu} neu, ${r.erweitert} erweitert. ${r.teamtage}`); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  return (
    <>
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis erledigt">{meldung}</div>}
      <Karte titel="Wochenraster: Team mal Tag, darin das Gebiet" rechts={
        <div className="reihe">
          <button type="button" className="knopf klein" onClick={() => schieben(-7)}>◀ Woche</button>
          <button type="button" className="knopf klein" onClick={() => setVon(heute)}>heute</button>
          <button type="button" className="knopf klein" onClick={() => schieben(7)}>Woche ▶</button>
          <button type="button" className="knopf klein haupt" onClick={neuPlanen}>Gebiete und Teamtage neu planen</button>
        </div>}>
        <p className="klein leise" style={{ marginTop: 0 }}>Die Planung legt Teamtage dort an, wo offene Aufträge liegen (automatisch, grau). Ein Klick auf eine Zelle setzt das Gebiet von Hand, sperrt den Tag (Urlaub, Krankheit) oder nimmt ihn weg. Zahl in der Zelle: gebuchte Termine von {d?.takt?.plaetze ?? 6}.</p>
        {d && !d.teams.length && <div className="leise klein">Kein aktives Team. Erst unter Firmen und Teams anlegen.</div>}
        {/* AUF DEM TELEFON EINE LISTE JE TEAM (.400). Marvin, 19.10.2026,
            Bildschirmfoto: das Raster wurde gestapelt und zeigte leere blaue
            Kästen. Jetzt: je Team seine Tage untereinander, Tag, Gebiet und
            Belegung in einer Zeile, Tippen öffnet dieselbe Zelle. */}
        {!!d?.teams?.length && (
          <div className="ne4-raster-mobil">
            {d.teams.map((tm: any) => (
              <div key={tm.id} className="ne4-raster-team">
                <div className="ne4-raster-teamname"><b>{tm.name}</b> <span className="klein leise">{tm.firma_kurz}{!tm.darf_hc ? ', kein HC' : ''}{!tm.darf_gwv ? ', kein GWV' : ''}</span></div>
                {tage.map((t) => {
                  const e = eintrag(tm.id, t);
                  return (
                    <button key={t} type="button" className={'ne4-raster-tag' + (e ? (e.gesperrt ? ' gesperrt' : e.automatisch ? ' auto' : ' hand') : '')}
                            onClick={() => setZelle({ team: tm, tag: t, alt: e ?? null })}>
                      <span className="ne4-raster-datum">{new Date(t + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
                      <span className="wachsen">{e ? (e.gesperrt ? <span className="leise">gesperrt{e.grund ? `: ${e.grund}` : ''}</span> : (e.gebiet ?? '?')) : <span className="leise">frei</span>}</span>
                      {e && !e.gesperrt && <span className="klein leise">{e.termine}/{e.plaetze}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {!!d?.teams?.length && (
          <div style={{ overflowX: 'auto' }} className="ne4-raster-breit">
            <table className="liste ne4-raster" style={{ minWidth: 900 }} data-stapeln="nein">
              <thead><tr><th>Team</th>{tage.map((t) => <th key={t} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{new Date(t + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</th>)}</tr></thead>
              <tbody>
                {d.teams.map((tm: any) => (
                  <tr key={tm.id} style={{ cursor: 'default' }}>
                    <td><b>{tm.name}</b><div className="klein leise">{tm.firma_kurz}{!tm.darf_hc ? ', kein HC' : ''}{!tm.darf_gwv ? ', kein GWV' : ''}</div></td>
                    {tage.map((t) => {
                      const e = eintrag(tm.id, t);
                      return (
                        <td key={t} style={{ textAlign: 'center', cursor: 'pointer', background: e?.gesperrt ? 'var(--grund)' : e ? (e.automatisch ? 'var(--akzent-leise)' : 'var(--laufend-flaeche, #eef7f3)') : undefined }}
                            onClick={() => setZelle({ team: tm, tag: t, alt: e ?? null })} title={e ? `${e.gebiet ?? 'ohne Gebiet'}${e.gesperrt ? ', gesperrt' : ''}` : 'frei'}>
                          {e ? (e.gesperrt ? <span className="klein leise">gesperrt</span> : <><div className="klein">{e.gebiet ?? '?'}</div><div className="klein leise">{e.termine}/{e.plaetze}</div></>) : <span className="leise">·</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!!d?.gebiete?.length && <p className="klein leise" style={{ marginTop: 8 }}>Offene Aufträge je Gebiet: {d.gebiete.map((g: any) => `${g.name} ${g.offen}`).join(', ')}</p>}
      </Karte>
      {zelle && d && <ZellenDialog zelle={zelle} gebiete={d.gebiete} zu={() => setZelle(null)} fertig={() => { setZelle(null); laden(); }} />}
    </>
  );
}

function ZellenDialog({ zelle, gebiete, zu, fertig }: { zelle: { team: any; tag: string; alt: any | null }; gebiete: any[]; zu: () => void; fertig: () => void }) {
  const [gebiet, setGebiet] = useState(zelle.alt?.gebiet_id ?? gebiete[0]?.id ?? '');
  const [gesperrt, setGesperrt] = useState<boolean>(!!zelle.alt?.gesperrt);
  const [grund, setGrund] = useState(zelle.alt?.grund ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.ne4TeamtagSetzen({ team_id: zelle.team.id, tag: zelle.tag, gebiet_id: gesperrt ? null : (gebiet || null), gesperrt, grund: grund || null }); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function weg() {
    setLaeuft(true); setFehler(null);
    try { await api.ne4TeamtagLoeschen(zelle.alt.id); fertig(); } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={`${zelle.team.name}, ${tagLang(zelle.tag)}`} breite={520} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {zelle.alt && zelle.alt.termine > 0 && <div className="hinweis warnung">{zelle.alt.termine} Termine sind an diesem Tag gebucht. Das Gebiet lässt sich ändern, der Tag nicht wegnehmen.</div>}
      <Haken name="gesperrt" titel="Tag sperren (Urlaub, Krankheit, Schulung)" wert={gesperrt} setze={setGesperrt} />
      {!gesperrt && (
        <div className="feld"><label>Gebiet</label>
          <select value={gebiet} onChange={(e) => setGebiet(e.target.value)}>
            <option value="">ohne Gebiet (Reserve)</option>
            {gebiete.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.offen} offen)</option>)}
          </select>
        </div>
      )}
      <Text name="grund" titel="Notiz" wert={grund} setze={setGrund} fehler={fehler} />
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        {zelle.alt && !(zelle.alt.termine > 0) && <button type="button" className="knopf" disabled={laeuft} onClick={weg}>Tag wegnehmen</button>}
        <button type="button" className="knopf haupt" disabled={laeuft} onClick={speichern}>Speichern</button>
      </div>
    </Dialog>
  );
}

/* =================================================================== Gebiete */

function Gebiete() {
  const [gebiete, setGebiete] = useState<any[]>([]);
  const [plz, setPlz] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [dialog, setDialog] = useState<any | null>(null);
  const laden = () => Promise.all([api.ne4Gebiete().then(setGebiete), api.ne4Plz().then(setPlz)]).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function schalten(g: any) {
    try { await api.ne4GebietAendern(g.id, { selbstbuchung: !g.selbstbuchung }); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  const ohne = plz.filter((p) => !p.gebiet);
  return (
    <>
      <Fehlerkasten lage={fehler} />
      <Karte titel={`Gebiete (${gebiete.length})`} rechts={<button type="button" className="knopf" onClick={() => setDialog({})}>Neues Gebiet</button>}>
        <p className="klein leise" style={{ marginTop: 0 }}>Ein Gebiet ist eine Gruppe von PLZ, die ein Team an einem Tag abfährt. Die Plattform bildet sie aus den Kunden; hier lassen sie sich ändern. <b>Selbstbuchung</b>: ob die Kundenseite in diesem Gebiet buchen lässt. Aus heißt: Ankündigung mit Rückrufbitte, Termine macht der Innendienst.</p>
        {!gebiete.length && <div className="leise klein">Noch kein Gebiet. Es entsteht mit dem ersten Import, sobald die Koordinaten da sind.</div>}
        {!!gebiete.length && (
          <table className="liste">
            <thead><tr><th>Gebiet</th><th>PLZ</th><th className="zahl">Offen</th><th className="zahl">Terminiert</th><th>Teamtage</th><th>Selbstbuchung</th><th /></tr></thead>
            <tbody>
              {gebiete.map((g) => (
                <tr key={g.id} style={{ cursor: 'default', opacity: g.aktiv ? 1 : 0.5 }}>
                  <td><b>{g.name}</b>{g.notiz && <div className="klein leise">{g.notiz}</div>}</td>
                  <td className="klein">{(g.plz ?? []).join(', ')}</td>
                  <td className="zahl">{g.offen}</td><td className="zahl">{g.terminiert}</td>
                  <td className="klein">{g.teamtage} geplant{g.naechster_tag ? <div className="leise">nächster {tagLang(String(g.naechster_tag).slice(0, 10))}</div> : null}</td>
                  <td><button type="button" className={`knopf klein ${g.selbstbuchung ? 'haupt' : ''}`} onClick={() => schalten(g)}>{g.selbstbuchung ? 'an: Kunde bucht selbst' : 'aus: Innendienst bucht'}</button></td>
                  <td className="knopfleiste"><button type="button" className="knopf klein" onClick={() => setDialog(g)}>ändern</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>
      <Karte titel={`Postleitzahlen der Kunden (${plz.length})`}>
        {!!ohne.length && <div className="hinweis warnung">{ohne.length} PLZ ohne Gebiet: {ohne.map((p) => p.plz).join(', ')}. Sie bekommen eines, sobald Koordinaten da sind, oder von Hand.</div>}
        <table className="liste">
          <thead><tr><th>PLZ</th><th>Ort</th><th className="zahl">Aufträge</th><th className="zahl">Offen</th><th>Gebiet</th></tr></thead>
          <tbody>{plz.map((p) => <tr key={p.plz} style={{ cursor: 'default' }}><td><code>{p.plz}</code></td><td>{p.ort}</td><td className="zahl">{p.auftraege}</td><td className="zahl">{p.offen}</td><td className="klein">{p.gebiet ?? <span className="leise">ohne</span>}</td></tr>)}</tbody>
        </table>
      </Karte>
      {dialog && <GebietDialog g={dialog.id ? dialog : null} zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />}
    </>
  );
}

function GebietDialog({ g, zu, fertig }: { g: any | null; zu: () => void; fertig: () => void }) {
  const [name, setName] = useState(g?.name ?? '');
  const [plz, setPlz] = useState((g?.plz ?? []).join(', '));
  const [selbst, setSelbst] = useState<boolean>(!!g?.selbstbuchung);
  const [aktiv, setAktiv] = useState<boolean>(g ? !!g.aktiv : true);
  const [notiz, setNotiz] = useState(g?.notiz ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    const liste = plz.split(/[\s,;]+/).filter(Boolean);
    try { if (g) await api.ne4GebietAendern(g.id, { name, plz: liste, selbstbuchung: selbst, aktiv, notiz: notiz || null }); else await api.ne4GebietAnlegen({ name, plz: liste, selbstbuchung: selbst, notiz: notiz || null }); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={g ? 'Gebiet ändern' : 'Neues Gebiet'} breite={560} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Text name="name" titel="Name" wert={name} setze={setName} pflicht fehler={fehler} />
      <Text name="plz" titel="Postleitzahlen" wert={plz} setze={setPlz} fehler={fehler} hinweis="mit Komma getrennt; eine PLZ gehört zu genau einem Gebiet" />
      <Haken name="selbst" titel="Selbstbuchung über die Kundenseite" wert={selbst} setze={setSelbst} />
      {g && <Haken name="aktiv" titel="Aktiv" wert={aktiv} setze={setAktiv} />}
      <Text name="notiz" titel="Notiz" wert={notiz} setze={setNotiz} fehler={fehler} />
      <div className="reihe" style={{ marginTop: 12 }}><button type="button" className="knopf" onClick={zu}>Abbrechen</button><button type="button" className="knopf haupt" disabled={laeuft || !name.trim()} onClick={speichern}>Speichern</button></div>
    </Dialog>
  );
}

/* ================================================================= Mailtexte */

export function Mailtexte({ ich }: { ich: Anmelder }) {
  const [texte, setTexte] = useState<any[]>([]);
  const [offen, setOffen] = useState<any | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const laden = () => api.ne4Mailtexte().then(setTexte).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  const stellt = darf(ich, 'ne4.konfiguration.pflegen');
  return (
    <>
      <Fehlerkasten lage={fehler} />
      {/* .586: ZWEI GRUPPEN, GEBÄUDE ZUERST. Marvin, 02.11.2026: „Wieso gibt es hier bei einer
          Terminbestätigung einen Link? ... Ist Hausbestätigung, ist das das, was für uns interessant ist?
          ... Das ist ein bisschen verwirrend." Die Gebäudemails gehen an den Eigentümer, die anderen an
          einzelne Kunden (HC/GWV). Der Link gehört nur zu Einzelterminen mit Selbstbuchung. */}
      {[
        { titel: 'Gebäude: Mails an den Eigentümer', wer: (t: any) => String(t.schluessel).startsWith('haus_'),
          text: 'Das ist der Weg für die Gebäudeaufträge aus dem Bulk-Portal. Die Terminbestätigung geht beim Buchen des Blocks raus, mit dem gefüllten Aushang als PDF; Aushang, Erinnerung und Nachfassen folgen der Wiedervorlage. Platzhalter: {anrede} {anschrift} {tage} {termin} {uhrzeit} {wohneinheiten} {etagen} {rueckruf} {kls} {firma}.' },
        { titel: 'Einzelaufträge (HC/GWV): Mails an einzelne Kunden', wer: (t: any) => !String(t.schluessel).startsWith('haus_'),
          text: 'Nur für einzelne Kundenaufträge, nicht für Gebäude. Der Buchungslink {link} wirkt nur in Gebieten mit Selbstbuchung; die ist überall aus, deshalb verweisen die Texte aufs Telefon. Platzhalter: {anrede} {anschrift} {art} {link} {termin} {uhrzeit} {firma} {telefon} {rueckruf} {kls}.' },
      ].map((gr) => (
        <Karte key={gr.titel} titel={gr.titel}>
          <p className="klein leise" style={{ marginTop: 0 }}>{gr.text}</p>
          <table className="liste">
            <thead><tr><th>Mail</th><th>Betreff</th><th>Aktiv</th><th /></tr></thead>
            <tbody>{texte.filter(gr.wer).map((t) => (
              <tr key={t.schluessel} style={{ cursor: 'default' }}>
                <td><b>{t.titel}</b><div className="klein leise"><code>{t.schluessel}</code></div></td><td className="klein">{t.betreff}</td>
                <td className="klein">{t.aktiv ? 'ja' : 'nein'}</td>
                <td className="knopfleiste">{stellt && <button type="button" className="knopf klein" onClick={() => setOffen(t)}>ändern</button>}</td>
              </tr>))}</tbody>
          </table>
        </Karte>
      ))}
      {offen && <MailDialog t={offen} zu={() => setOffen(null)} fertig={() => { setOffen(null); laden(); }} />}
    </>
  );
}

function MailDialog({ t, zu, fertig }: { t: any; zu: () => void; fertig: () => void }) {
  const [betreff, setBetreff] = useState(t.betreff);
  const [text, setText] = useState(t.text);
  const [aktiv, setAktiv] = useState<boolean>(!!t.aktiv);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.ne4MailtextSetzen(t.schluessel, { betreff, text, aktiv }); fertig(); } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    /* .585: GROSS GENUG FÜR DEN GANZEN TEXT. Marvin, 02.11.2026: „die Fenster sind viel zu klein. Ich will
       die entsprechend groß haben, damit man das direkt alles sauber lesen kann und nicht hier gescrollt
       werden muss." Das Feld ist so hoch wie der Text (plus zwei Zeilen zum Weiterschreiben). */
    <Dialog titel={t.titel} breite={1100} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Text name="betreff" titel="Betreff" wert={betreff} setze={setBetreff} pflicht fehler={fehler} breite="100%" />
      <Text name="text" titel="Text" wert={text} setze={setText} pflicht fehler={fehler} mehrzeilig breite="100%" zeilen={Math.max(10, String(text ?? '').split('\n').reduce((n: number, z: string) => n + Math.max(1, Math.ceil(z.length / 95)), 0) + 2)} />
      <div className="klein leise" style={{ marginTop: -4, marginBottom: 6 }}>Ohne Grußformel: Gruß und Firma hängt der Mailweg mit der Signatur an.</div>
      <Haken name="aktiv" titel="Diese Mail wird gesendet" wert={aktiv} setze={setAktiv} />
      <div className="reihe" style={{ marginTop: 12 }}><button type="button" className="knopf" onClick={zu}>Abbrechen</button><button type="button" className="knopf haupt" disabled={laeuft} onClick={speichern}>Speichern</button></div>
    </Dialog>
  );
}

/* ===================================================================== Läufe */

export function Laeufe() {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const laden = () => api.ne4Automatik().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  async function jetzt(s: string) { setMeldung(null); try { const b = await api.ne4AutomatikJetzt(s); setMeldung(b.meldung); await laden(); } catch (f) { setFehler(ausFehler(f)); } }
  async function schalten(z: any) { try { await api.ne4AutomatikAktiv(z.schluessel, !z.aktiv); await laden(); } catch (f) { setFehler(ausFehler(f)); } }
  return (
    <>
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis erledigt">{meldung}</div>}
      <Karte titel="Was von selbst läuft (NE4)">
        <table className="liste">
          <thead><tr><th>Lauf</th><th>Takt</th><th>Zuletzt</th><th>Aktiv</th><th /></tr></thead>
          <tbody>{(d?.zeilen ?? []).map((z: any) => (
            <tr key={z.schluessel} style={{ cursor: 'default' }}>
              <td><b>{z.name}</b><div className="klein leise">{z.zweck}</div></td>
              <td className="klein">{z.takt}{z.uhrzeit ? ` ${String(z.uhrzeit).slice(0, 5)}` : ''}</td>
              <td className="klein">{z.zuletzt_am ? <>{zeitpunkt(z.zuletzt_am)}<div className="leise">{z.zuletzt_meldung}</div></> : <span className="leise">noch nie</span>}</td>
              <td><button type="button" className={`knopf klein ${z.aktiv ? 'haupt' : ''}`} onClick={() => schalten(z)}>{z.aktiv ? 'an' : 'aus'}</button></td>
              <td className="knopfleiste"><button type="button" className="knopf klein" onClick={() => jetzt(z.schluessel)}>jetzt laufen</button></td>
            </tr>))}</tbody>
        </table>
      </Karte>
      {!!d?.letzte?.length && (
        <Karte titel="Die letzten Läufe">
          <table className="liste"><tbody>{d.letzte.map((l: any, i: number) => <tr key={i} style={{ cursor: 'default' }}><td className="klein">{zeitpunkt(l.begonnen_am)}</td><td className="klein">{l.schluessel}</td><td className="klein">{l.meldung}</td></tr>)}</tbody></table>
        </Karte>
      )}
    </>
  );
}

/* ====================================================================== GBGS */

const GBGS_ART: Record<string, string> = { termin: 'Termin eintragen', termin_aendern: 'Termin ändern', termin_absagen: 'Termin löschen', wartegrund: 'Wartegrund setzen', rueckgabe: 'Rückgabe mit Wartegrund', fertig: 'Fertigmeldung' };

export function Ne4Gbgs({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [alle, setAlle] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const stellt = darf(ich, 'ne4.konfiguration.pflegen');
  const laden = () => api.ne4Gbgs(alle).then((x) => { setD(x); setWerte(Object.fromEntries(x.einstellungen.filter((e: any) => !e.geheim).map((e: any) => [e.schluessel, e.wert ?? '']))); }).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [alle]);
  useFrisch(laden);
  const zeile = (z: any) => {
    const n = z.nutzlast ?? {};
    const kunde = `${z.kls_id}, ${[z.vorname, z.name].filter(Boolean).join(' ')}, ${z.strasse ?? ''} ${z.hausnummer ?? ''}, ${z.plz ?? ''} ${z.ort ?? ''}, ${z.auftragsart}`;
    if (z.art === 'termin' || z.art === 'termin_aendern') return `${kunde}, Termin ${n.tag ? tagLang(n.tag) : ''} ${uhr(n.beginn)} Uhr${n.vorher ? ` (vorher ${n.vorher})` : ''}`;
    if (z.art === 'termin_absagen') return `${kunde}, Termin löschen${n.grund ? `: ${n.grund}` : ''}`;
    if (z.art === 'fertig') return `${kunde}, ausgeführt am ${n.am ?? ''}`;
    return `${kunde}, ${GBGS_ART[z.art] ?? z.art}${n.grund ? `: ${n.grund}` : ''}`;
  };
  const kopieren = (t: string) => { try { navigator.clipboard.writeText(t); setMeldung('In der Zwischenablage.'); } catch { setMeldung(t); } };
  async function haken(z: any, verworfen = false) { try { await api.ne4GbgsErledigt(z.id, null, verworfen); await laden(); } catch (f) { setFehler(ausFehler(f)); } }
  async function speichern(e: any) {
    setFehler(null); setMeldung(null);
    try { await api.ne4KonfigurationSetzen(e.schluessel, werte[e.schluessel] ?? null); setMeldung(`${e.titel} gespeichert.`); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  async function geheim(e: any) {
    const w = await eingeben(`${e.titel} eingeben (wird verschlüsselt abgelegt, nie angezeigt):`, '', { titel: e.titel, geheim: true });
    if (w === null) return;
    try { await api.ne4KonfigurationGeheim(e.schluessel, w || null); setMeldung(`${e.titel} ${w ? 'gesetzt' : 'gelöscht'}.`); await laden(); } catch (f) { setFehler(ausFehler(f)); }
  }
  const offen = (d?.zeilen ?? []).filter((z: any) => ['hand', 'wartet', 'laeuft'].includes(z.zustand));
  /* .599: AUFBAU WIE DIE SEITE GBGS IN NE3. Marvin, 02.11.2026: „Hier bei GBGS wünsche ich mir den gleichen Aufbau wie
     bei NE3. Das heißt, da, wo es hängen geblieben ist, ich kann es manuell anstoßen, die Erledigten, ja, identischer
     Aufbau." Oben der Stand (gelesen, Roboter, scharf), Kacheln je Zustand der Roboterzeilen, die Warteschlange mit
     Bild, „Noch einmal", „Erledigt", „Verwerfen" (dieselbe Tabelle wie NE3), darunter die Handliste. */
  const siehtSchlange = darf(ich, 'gbgs.lesen');
  const [schlange, setSchlange] = useState<any[]>([]);
  const [stand, setStand] = useState<any>(null);
  const [bild, setBild] = useState<string | null>(null);
  const [liest, setLiest] = useState(false);
  const ladenSchlange = async () => {
    if (!siehtSchlange) return;
    try {
      const [q, a] = await Promise.all([api.gbgsWarteschlange(), api.ne4Auftraege(new URLSearchParams({ zustand: 'alle', suche: '' }))]);
      setSchlange((q ?? []).filter((x: any) => x.art === 'ne4_bulk_termin' || x.art === 'auskundung_holen'));
      setStand(a);
    } catch (f) { setFehler(ausFehler(f)); }
  };
  useEffect(() => { void ladenSchlange(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useFrisch(ladenSchlange);
  const tunSchlange = async (f: () => Promise<any>) => {
    setFehler(null);
    try { const r = await f(); if (r?.klartext) setMeldung(r.klartext); await ladenSchlange(); await laden(); } catch (x) { setFehler(ausFehler(x)); }
  };
  const k = {
    offen: schlange.filter((q) => q.zustand === 'wartet' || q.zustand === 'laeuft'),
    haengt: schlange.filter((q) => q.zustand === 'fehler' || q.zustand === 'hand'),
    probelauf: schlange.filter((q) => q.zustand === 'probelauf'),
    erledigt: schlange.filter((q) => q.zustand === 'erledigt'),
    alle: schlange,
  };
  type HFilter = 'offen' | 'ohne_termin' | 'wartet' | 'wartegrund' | 'auskundung' | 'passt_nicht' | 'terminiert' | 'haengt' | 'alle';
  const [hFilter, setHFilter] = useState<HFilter>('offen');
  const [hSuche, setHSuche] = useState('');
  const haeuser: any[] = (stand?.zeilen ?? []).filter((a: any) => a.art === 'GEB' && a.bulk_kurz?.zustand !== undefined && a.gbgs_gebaeudeauftrag_id);
  const istOffen = (a: any) => !['ausgefuehrt', 'abgerechnet', 'zurueckgegeben'].includes(a.zustand);
  const passt: Record<HFilter, (a: any) => boolean> = {
    offen: istOffen,
    ohne_termin: (a) => istOffen(a) && a.bulk_kurz?.zustand === 'waehlbar' && !(a.connect_termine?.n > 0),
    wartet: (a) => a.bulk_kurz?.zustand === 'wartet',
    wartegrund: (a) => !!a.bulk_kurz?.wartegrund,
    auskundung: (a) => !!a.bulk_kurz?.auskundung_neu,
    passt_nicht: (a) => connectAbgleich(a, a.bulk_kurz?.dosen).passt === false,
    terminiert: (a) => a.connect_termine?.n > 0,
    haengt: (a) => schlange.some((q) => q.kls_id === a.kls_id && (q.zustand === 'fehler' || q.zustand === 'hand')),
    alle: () => true,
  };
  const hz = Object.fromEntries(Object.entries(passt).map(([f, fn]) => [f, haeuser.filter(fn).length])) as Record<HFilter, number>;
  const [wg, setWg] = useState<any | null>(null);
  const wartegrundOeffnen = async (a: any) => {
    try {
      const z = ((await api.gbgsStandorte('alle', String(a.kls_id))) ?? []).find((x: any) => String(x.kls_id) === String(a.kls_id)) ?? null;
      const hb = { ...(z?.bulk ?? {}), gebaeudeauftrag_id: a.gbgs_gebaeudeauftrag_id, strasse: a.strasse, hausnummer: a.hausnummer, plz: a.plz, ort: a.ort };
      setWg({ bauaufgaben: [], bauauftraege: [], gebaeudeteile: [], ...(z ?? {}), kls_id: a.kls_id, bulk: hb });
    } catch (f) { setFehler(ausFehler(f)); }
  };
  const hk = (f: HFilter, titel: string, zahl: number, text: string, warn = false) => (
    <button type="button" className={`schrittkachel ${hFilter === f ? 'an' : ''} ${warn && zahl ? 'warn' : ''}`} onClick={() => setHFilter(f)} aria-pressed={hFilter === f}>
      <span className="titel">{titel}</span><span className="zahl">{stand ? zahl : '…'}</span><span className="klein leise">{text}</span>
    </button>
  );
  const hZeilen = haeuser.filter(passt[hFilter]).filter((a) => !hSuche.trim() || hSuche.toLowerCase().split(/\s+/).every((w) =>
      `${a.kls_id} ${a.strasse} ${a.hausnummer} ${a.plz} ${a.ort} ${a.eigentuemer_org ?? ''} ${a.name ?? ''}`.toLowerCase().includes(w)))
    .sort((x, y) => String(x.connect_termine?.ab ?? '9999').localeCompare(String(y.connect_termine?.ab ?? '9999')) || String(x.strasse).localeCompare(String(y.strasse), 'de'));
  const ein = (d?.einstellungen ?? []) as any[];
  const an = (s: string) => ein.find((e) => e.schluessel === s)?.wert === 'ja';
  const gz = stand?.zaehler ?? stand?.zahlen ?? {};
  return (
    <>
      <Kopf titel="NE4, GBGS" weg="Die Gebäudeaufträge im Bulk-Portal und der Roboter"
            rechts={darf(ich, 'ne4.auftraege.pflegen') && (
              <button type="button" className="knopf klein" disabled={liest}
                      onClick={async () => { setLiest(true); try { const r = await api.ne4GbgsLesen(); setMeldung(r.klartext); await ladenSchlange(); } catch (f) { setFehler(ausFehler(f)); } finally { setLiest(false); } }}>
                {liest ? 'Liest …' : 'Jetzt lesen'}
              </button>)} />
      <div className="inhalt">
      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis erledigt">{meldung}</div>}
      {/* .609: DER AUFBAU DER NE3-SEITE GBGS, Stück für Stück. Marvin, 03.11.2026 (mit den Bildern der NE3-Seite):
          „Genau den gleichen Aufbau wünsche ich mir im NE4-Bereich." Oben groß, wann gelesen wurde, mit den Zahlen;
          darunter Lesen, Roboter, scharf; die Kacheln filtern die Häuser; die Warteschlange zeigt alle Zeilen mit Bild
          und „Alle noch einmal"; darunter „Häuser", eine Zeile je Gebäudeauftrag mit Suche und denselben Filtern. */}
      <div className="gbgs-stand">
        {stand?.gbgs?.stand_am ? <b>Zuletzt gelesen {zeitpunkt(stand.gbgs.stand_am)} Uhr</b> : <b>Noch nicht gelesen.</b>}
        <span className="klein leise">{haeuser.length ? `, ${haeuser.length} Häuser im Bulk-Portal, ${haeuser.reduce((n, a) => n + (a.bulk_kurz?.dosen ?? 0), 0)} Connects` : ''}</span>
      </div>
      <div className="klein leise" style={{ marginBottom: 8 }}>
        {stand?.gbgs?.zuletzt ? `${stand.gbgs.zuletzt}, ` : ''}Roboter {an('gbgs.roboter') ? 'an' : 'aus'}{an('gbgs.roboter') ? (an('gbgs.scharf') ? ', setzt die Termine im Portal' : ', Probelauf („scharf" ist aus)') : ''}, Zugang, Roboter und „scharf“: NE4, Konfiguration, Reiter GBGS
      </div>
      <div className="schrittkacheln gbgs-kacheln">
        {hk('offen', 'Häuser offen', hz.offen, 'noch nicht fertig gebaut')}
        {hk('ohne_termin', 'Wählbar ohne Termin', hz.ohne_termin, 'Portal lässt Termine zu, keiner gesetzt', true)}
        {hk('wartet', 'NE4 wartet', hz.wartet, 'Portal lässt keinen Termin zu')}
        {hk('wartegrund', 'Mit Wartegrund', hz.wartegrund, 'Wartegrund im Portal')}
        {hk('auskundung', 'Neue Auskundung', hz.auskundung, 'im Portal neu angestoßen', true)}
        {hk('passt_nicht', 'Connects passen nicht', hz.passt_nicht, 'Portal und Einheiten weichen ab', true)}
        {hk('terminiert', 'Terminiert', hz.terminiert, 'Connects mit Termin')}
        {hk('haengt', 'Hängt beim Roboter', k.haengt.length, 'nicht geschafft oder von Hand', true)}
      </div>
      {siehtSchlange && (
        <Karte titel={`Warteschlange (${k.offen.length} läuft oder wartet, ${k.haengt.length} von Hand)`}
               rechts={<span className="reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                 <span className="klein leise">Was der Roboter im Portal tut oder getan hat, je Zeile mit Bild</span>
                 {darf(ich, 'gbgs.eintragen') && (k.haengt.length + k.probelauf.length) > 0 && (
                   <button className="knopf klein" onClick={async () => {
                     if (await fragen(`${k.haengt.length + k.probelauf.length} hängende Zeilen und Probeläufe noch einmal an den Roboter?`)) {
                       void tunSchlange(async () => { for (const q of [...k.haengt, ...k.probelauf]) await api.gbgsNochmal(q.id); return { klartext: 'Zurück an den Roboter.' }; });
                     }
                   }}>Alle noch einmal ({k.haengt.length}{k.probelauf.length ? ` + ${k.probelauf.length} Probeläufe` : ''})</button>)}
               </span>}>
          <SchlangeTabelle ich={ich} schlange={schlange} tun={tunSchlange} setBild={setBild} leer="Keine Zeile in der Warteschlange." />
        </Karte>
      )}
      <Karte titel="Häuser" rechts={<span className="klein leise">eine Zeile je Gebäudeauftrag, der früheste Termin zuerst</span>}>
        <div className="reihe anstehendfilter" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Suchfeld wert={hSuche} setze={setHSuche} mehr="Eigentümer" breit={320} />
          {([['offen', 'offen'], ['ohne_termin', 'wählbar ohne Termin'], ['wartet', 'NE4 wartet'], ['wartegrund', 'mit Wartegrund'], ['auskundung', 'neue Auskundung'],
             ['passt_nicht', 'Connects passen nicht'], ['terminiert', 'terminiert'], ['alle', 'alle']] as const).map(([f, x]) => (
            <button key={f} type="button" className={`knopf klein ${hFilter === f ? 'haupt' : ''}`} aria-pressed={hFilter === f} onClick={() => setHFilter(f)}>{x}</button>
          ))}
        </div>
        {!stand ? <p className="klein leise">Wird geladen.</p> : !hZeilen.length ? <p className="klein leise">Kein Haus passt zu Suche und Filter.</p> : (
          <div className="anstehendrahmen">
            <table className="liste anstehend gbgs-liste">
              <thead><tr><th>Haus</th><th>Bulk-Portal</th><th>Termin</th><th></th></tr></thead>
              <tbody>
                {hZeilen.map((a) => {
                  const b = a.bulk_kurz ?? {};
                  const ab = connectAbgleich(a, b.dosen);
                  const inSchlange = schlange.filter((q) => q.kls_id === a.kls_id && ['wartet', 'laeuft', 'fehler', 'hand'].includes(q.zustand)).length;
                  return (
                    <tr key={a.id} style={{ cursor: 'default' }}>
                      <td>
                        <a className="alslink" href={`#/a/ne4/${encodeURIComponent(String(a.kls_id ?? a.id))}`}><b>{a.strasse} {a.hausnummer}, {a.plz} {a.ort}</b></a>
                        <div className="klein leise">KLS {a.kls_id}{a.eigentuemer_org ? `, ${a.eigentuemer_org}` : ''}</div>
                        <div className="klein leise"><span className="merker">Bulk</span> {a.wohneinheiten ?? 0} WE{a.ge ? `, ${a.ge} GE` : ''}{a.sl ? `, ${a.sl} SL` : ''}</div>
                      </td>
                      <td>
                        <div className="gbgs-aufgaben">
                          <div className="gbgs-aufgabe">
                            <span className={`marke-zustand ${b.zustand === 'waehlbar' ? 'z-erledigt' : b.zustand === 'wartet' ? 'z-ueberfaellig' : 'z-laufend'}`}>NE4: {b.zustand === 'waehlbar' ? 'wählbar' : b.zustand === 'wartet' ? 'warten' : b.zustand ?? 'kein NE4-Auftrag'}</span>
                            {b.zustand === 'wartet' && b.grund_text && <span className="klein"> {b.grund_text}</span>}
                          </div>
                          <div className="klein">Connects im Portal: {b.dosen ?? 0}, davon {b.gebaut ?? 0} gebaut, {b.terminiert ?? 0} terminiert{ab.passt === false && <span className="rot">, passen nicht zu {ab.haus} Einheiten</span>}</div>
                          {b.wartegrund && <div className="klein">Wartegrund: {b.wartegrund_text || b.wartegrund}</div>}
                          {b.auskundung_neu && <div className="klein rot">Neue Auskundung angestoßen</div>}
                          {b.gfta_enddatum && <div className="klein leise">GFTA-Enddatum {datum(b.gfta_enddatum)}</div>}
                        </div>
                      </td>
                      <td className="klein">{a.connect_termine?.n > 0
                        ? <>{datum(a.connect_termine.ab)}{a.connect_termine.bis !== a.connect_termine.ab ? ` bis ${datum(a.connect_termine.bis)}` : ''}{a.connect_termine.von ? <div>{a.connect_termine.von} bis {a.connect_termine.bis_uhr} Uhr</div> : null}
                            {a.termin && <div>{a.termin.firma}<div className="leise">{a.termin.team}</div></div>}
                            {a.connect_termine.n < a.connect_termine.alle && <div className="leise">{a.connect_termine.n} von {a.connect_termine.alle} Connects</div>}</>
                        : <span className="leise">kein Termin</span>}</td>
                      <td className="knopfleiste">
                        <a className="knopf klein" href={`#/a/ne4/${encodeURIComponent(String(a.kls_id ?? a.id))}`}>Auftrag öffnen</a>
                        {/* .610: der Wartegrund wie in NE3 (Recht „GBGS eintragen"), am Gebäudeauftrag im Bulk-Portal. */}
                        {darf(ich, 'gbgs.eintragen') && a.gbgs_gebaeudeauftrag_id && <button type="button" className="knopf klein" onClick={() => void wartegrundOeffnen(a)}>Wartegrund</button>}
                        {inSchlange > 0 && <span className="klein leise">{inSchlange} in der Warteschlange</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
      {wg && <WartegrundFenster standort={wg} ich={ich} zu={() => setWg(null)} fertig={(x) => { setWg(null); setMeldung(x); void ladenSchlange(); }} />}
      {bild && <Dateifenster datei={{ id: bild, dateiname: 'Bild aus dem GBGS.png', mime: 'image/png', adresse: `/api/gbgs/bild/${bild}` }} zu={() => setBild(null)} />}
      <Karte titel="Handliste: was ins GBGS gehört"
             rechts={<label className="klein" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> auch erledigte</label>}>
        <p className="klein leise" style={{ marginTop: 0 }}>Jede Buchung, Änderung, Absage, Fertigmeldung und Rückgabe steht hier als Zeile zum Kopieren. Abhaken, sobald es im GBGS steht. Termine der Gebäude im Bulk-Portal übernimmt der Roboter (Warteschlange oben); hier bleibt, was er nicht kann (Absagen, Kunden-Termine).</p>
        {d && !offen.length && !alle && <div className="leise klein">Nichts offen.</div>}
        <table className="liste">
          <thead><tr><th>Seit</th><th>Was</th><th>Zeile</th><th /></tr></thead>
          <tbody>{(d?.zeilen ?? []).map((z: any) => (
            <tr key={z.id} style={{ cursor: 'default', opacity: ['erledigt', 'verworfen'].includes(z.zustand) ? 0.55 : 1 }}>
              <td className="klein">{zeitpunkt(z.created_at)}{z.zustand === 'wartet' && <div className="leise">Roboter</div>}{z.zustand === 'erledigt' && <div className="leise">erledigt {zeitpunkt(z.erledigt_am)}{z.erledigt_von ? `, ${z.erledigt_von}` : ''}</div>}{z.zustand === 'verworfen' && <div className="leise">verworfen</div>}</td>
              <td className="klein"><b>{GBGS_ART[z.art] ?? z.art}</b></td>
              <td className="klein">{zeile(z)}</td>
              <td className="knopfleiste breit">
                <button type="button" className="knopf klein" onClick={() => kopieren(zeile(z))}>kopieren</button>
                {/* .601: Roboterzeilen (wartet) werden oben in der Warteschlange erledigt, nicht hier abgehakt. */}
                {z.zustand === 'wartet' && <span className="klein leise">beim Roboter, siehe Warteschlange</span>}
                {z.zustand === 'hand' && <button type="button" className="knopf klein haupt" onClick={() => haken(z)}>eingetragen</button>}
                {z.zustand === 'hand' && <button type="button" className="knopf klein" onClick={() => haken(z, true)}>verwerfen</button>}
              </td>
            </tr>))}</tbody>
        </table>
      </Karte>
      </div>
    </>
  );
}
